import gql from 'graphql-tag';
import { IBaseSlice, SliceTypes } from '..';
import { fetchArticles, IAllArticles, IArticle, parseArticles } from '../../articles';
import { RichTextBlock } from 'prismic-reactjs';
import { parser } from '../../utils/parsers';
import btoa from 'btoa';
import { PrismicImage } from '../../utils/PrismicTypes';

export interface IArticleIndex extends IBaseSlice {
  title: string;
  lang?: string;
  tagline?: string;
  tags: string[];
  matchAll?: boolean;
  featured: IArticle;
  filter: string[];
  items: IAllArticles;
}

export const fragment = (name: string, type: string, fragmentName: string) => gql`
  fragment ${fragmentName || name} on ${type} {
    ... on ${type}${name} {
      type

      primary {
        slice_title
        slice_tagline
        slice_tags
        slice_logic
        slice_filter
        slice_featured {
          ... on Article {
            date
            title
            tagline
            image
            caption
            summary

            _meta {
              uid
              type
              lang
              tags
            }
          }
        }
      }
    }
  }
`;

type NestedProps = {
  lang: string;
  limit?: number;
  previewData: boolean;
  redirects: Array<{ from: string; to: string }>;
  tags?: string[];
  tagsIn?: string[];
  filter?: string[];
  after?: any;
  page?: any;
};

export const fetchArticlesAndParse = async ({
  lang,
  limit = 9,
  previewData,
  redirects = [],
  tags,
  tagsIn,
  page = undefined,
  after = undefined,
}: NestedProps) => {
  const endCursor =
    page && page !== 1 && !after ? btoa(`arrayconnection:${(page - 1) * limit - 1}`) : after;

  const items = await fetchArticles(lang, endCursor, previewData, 9, tags, tagsIn);

  return parseArticles(items, redirects, lang);
};

type Props = {
  primary?: {
    slice_title: RichTextBlock[];
    slice_tagline: RichTextBlock[];
    slice_tags: string;
    slice_logic: boolean;
    slice_filter: string;
    slice_featured: {
      date: string;
      title: RichTextBlock[];
      tagline: string;
      image: PrismicImage;
      caption: RichTextBlock[];
      summary: RichTextBlock[];
      _meta: {
        uid: string;
        type: string;
        lang: string;
        tags: string[];
      };
    };
  };
};

export const parse = (slice: Props, redirects, lang): IArticleIndex => ({
  type: SliceTypes.ArticleIndex,
  lang,
  tagline: parser.string(slice.primary?.slice_tagline),
  title: parser.string(slice.primary?.slice_title),
  tags: (parser.string(slice.primary?.slice_tags) || '')
    .split(',')
    .map((str) => str.trim())
    .filter((x) => !!x),
  matchAll: slice.primary?.slice_logic || false,
  filter: (parser.string(slice.primary?.slice_filter) || '')
    .split(',')
    .map((str) => str.trim())
    .filter((x) => !!x),
  featured: slice.primary?.slice_featured
    ? {
        uid: slice.primary?.slice_featured?._meta.uid || null,
        date: parser.dateTime(slice.primary?.slice_featured?.date, undefined, lang),
        title: parser.string(slice.primary?.slice_featured?.title),
        tagline: parser.string(slice.primary?.slice_featured?.tagline),
        image: parser.image(slice.primary?.slice_featured?.image),
        caption: parser.string(slice.primary?.slice_featured?.caption),
        summary: parser.string(slice.primary?.slice_featured?.summary),
        link: parser.linkFromMeta(slice.primary?.slice_featured?._meta, redirects),
        tags: slice.primary?.slice_featured?._meta?.tags ?? [],
      }
    : null,
  items: { total: 0, items: [] },
});
